/// <reference types="@angular/localize" />

import { bootstrapApplication } from '@angular/platform-browser';
import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { loadTranslations } from '@angular/localize';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';


type SupportedLocale = 'en' | 'es' | 'pt';

const supportedLocales: SupportedLocale[] = ['en', 'es', 'pt'];

function detectLocale(): SupportedLocale {
  const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
  if (supportedLocales.includes(firstSegment as SupportedLocale)) {
    return firstSegment as SupportedLocale;
  }

  const stored = localStorage.getItem('locale');
  if (supportedLocales.includes(stored as SupportedLocale)) {
    return stored as SupportedLocale;
  }

  return 'en';
}

function parseXlfTranslations(xlfContent: string): Record<string, string> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xlfContent, 'application/xml');
  const translationMap: Record<string, string> = {};

  const serializeTarget = (target: Element): string => {
    let text = '';
    target.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent ?? '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        const tag = el.tagName.toLowerCase();
        const placeholderId = el.getAttribute('id');

        // Preserve dynamic placeholders so Angular can replace them at runtime.
        if (placeholderId && (tag === 'x' || tag === 'ph' || tag === 'bx' || tag === 'ex')) {
          text += `{$${placeholderId}}`;
        } else {
          text += serializeTarget(el);
        }
      }
    });
    return text;
  };

  const units = doc.querySelectorAll('unit[id], trans-unit[id]');
  units.forEach((unit) => {
    const id = unit.getAttribute('id');
    const target = unit.querySelector('segment > target') || unit.querySelector('target');
    const text = target ? serializeTarget(target).trim() : undefined;
    if (id && text) {
      translationMap[id] = text;
    }
  });

  return translationMap;
}

async function loadLocaleData(locale: SupportedLocale): Promise<void> {
  if (locale === 'en') return;
  if (locale === 'es') {
    const data = (await import('@angular/common/locales/es')).default;
    registerLocaleData(data);
  } else if (locale === 'pt') {
    const data = (await import('@angular/common/locales/pt')).default;
    registerLocaleData(data);
  }
}

async function bootstrapWithLocale() {
  const locale = detectLocale();

  if (locale !== 'en') {
    try {
      const response = await fetch(`/locale/messages.${locale}.xlf`);
      if (response.ok) {
        const xlf = await response.text();
        const translations = parseXlfTranslations(xlf);
        loadTranslations(translations);
      } else {
        console.warn(`Could not load translations for locale ${locale}. Status: ${response.status}`);
      }
    } catch (error) {
      console.warn(`Failed to load translations for locale ${locale}:`, error);
    }
  }

  await loadLocaleData(locale);
  document.documentElement.lang = locale;

  const localeProviders = locale === 'en' ? [] : [{ provide: LOCALE_ID, useValue: locale }];

  bootstrapApplication(AppComponent, {
    ...appConfig,
    providers: [...(appConfig.providers || []), ...localeProviders],
  }).catch((err) => console.error(err));
}

bootstrapWithLocale();
